import { ColumnDef } from "@tanstack/react-table";
import * as Xstate from "@xstate/react";
import classNames from "classnames";
import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiDownload } from "react-icons/fi";
import { TbFileSearch } from "react-icons/tb";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { GlobalContext } from "../../../../app/stateMachines/GlobalContext";
import { Card } from "../../../../components/card/Card";
import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import PreviewFile from "../../../../components/modal/PreviewFile";
import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import NoItems from "../../../../components/NoItems";
import ContactFileSvg from "../../../../components/svg/ContactFileSvg";
import PlusSvg from "../../../../components/svg/PlusSvg";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { WarningExclaimSvg } from "../../../../components/svg/WarningExclaimSvg";
import { Table } from "../../../../components/table/Table";
import { ContactFileModel } from "../../../../factories/contacts/models/contact-file.model";
import {
  useAddWorkFileMutation,
  useDeleteWorkFileMutation,
  useGetWorkFilesQuery,
  useLazyGetPreviewWorkFileQuery,
} from "../../../../slices/FilesSlice";
import { InputFile } from "../../../contacts/components/InputFile";

type WorkFileTableItem = ContactFileModel & {
  documentName?: string | null;
  documentURI?: string | null;
  documentSignedUrl?: string | null;
  documentSize?: number | null;
  createdDate?: string | null;
  originalFileName?: string | null;
};

const FILE_NAME_TOOLTIP_ID = "work-file-name-tooltip";

const getFileName = (file: WorkFileTableItem) =>
  file.originalFileName || file.documentName || "Unknown file";

const getFileNameWithoutExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf(".");

  if (lastDotIndex <= 0) {
    return fileName;
  }

  return fileName.slice(0, lastDotIndex);
};

const getFileExtension = (fileName: string) => {
  const extension = fileName.split(".").pop();

  return extension ? extension.toUpperCase() : "-";
};

export const formatFileSize = (sizeInBytes?: number | null) => {
  if (!sizeInBytes && sizeInBytes !== 0) {
    return "-";
  }

  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  }

  const sizeInKb = sizeInBytes / 1024;

  if (sizeInKb < 1024) {
    return `${Math.round(sizeInKb)} KB`;
  }

  return `${(sizeInKb / 1024).toFixed(1)} MB`;
};

const formatDate = (date?: string | null) => {
  if (!date) {
    return "-";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(new Date(date));
};

const getFileSavePickerType = (fileName: string, blob: Blob) => {
  const extension = fileName.includes(".")
    ? `.${fileName.split(".").pop()?.toLowerCase()}`
    : "";

  if (!extension) {
    return undefined;
  }

  return [
    {
      description: "File",
      accept: {
        [blob.type || "application/octet-stream"]: [extension],
      },
    },
  ];
};

const fallbackDownloadFile = (blob: Blob, fileName: string) => {
  const objectUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(objectUrl);
};

export const downloadFileByUrl = async (url: string, fileName: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Failed to download file");
  }

  const blob = await response.blob();

  if (!window.showSaveFilePicker) {
    fallbackDownloadFile(blob, fileName);
    return;
  }

  try {
    const fileHandle = await window.showSaveFilePicker({
      suggestedName: fileName,
      startIn: "downloads",
      types: getFileSavePickerType(fileName, blob),
    });

    const writable = await fileHandle.createWritable();

    await writable.write(blob);
    await writable.close();
  } catch (error) {
    const isUserCancel =
      error instanceof DOMException && error.name === "AbortError";

    if (isUserCancel) {
      return;
    }

    fallbackDownloadFile(blob, fileName);
  }
};

export const WorkFiles: FC = () => {
  const { workId } = useParams();

  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;

  const {
    data: workFiles,
    isLoading,
    refetch,
  } = useGetWorkFilesQuery(workId || "", { skip: !workId });

  const [getPreviewFile, { data: previewFile, isLoading: isLoadingPreview }] =
    useLazyGetPreviewWorkFileQuery();

  const [getDownloadFile] = useLazyGetPreviewWorkFileQuery();

  const [addFile, { isLoading: isLoadingAdd }] = useAddWorkFileMutation();

  const [deleteFile, { isLoading: isLoadingDelete }] =
    useDeleteWorkFileMutation();

  const addFileRef = useRef<HTMLInputElement>(null);

  const [activeFileName, setActiveFileName] = useState("");
  const [error, setError] = useState("");
  const [isOpenPreview, setIsOpenPreview] = useState(false);
  const [openDelete, setOpenDelete] = useState<WorkFileTableItem | null>(null);
  const [isOpenErrorPopUp, setIsOpenErrorPopUp] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );

  const filesData = useMemo<WorkFileTableItem[]>(
    () => (workFiles ?? []) as WorkFileTableItem[],
    [workFiles],
  );

  const handleFileChange = useCallback(
    (files: File | File[]) => {
      if (!workId) {
        return;
      }

      const filesArray = Array.isArray(files) ? files : [files];

      void (async () => {
        let failedUploads = 0;

        for (const file of filesArray) {
          const formData = new FormData();

          formData.append("Documents", file);
          formData.append("workId", workId);

          if (userInfoByEmail?.id) {
            formData.append("assignedUserRelayId", userInfoByEmail.id);
          }

          formData.append("notes", "");

          try {
            await addFile(formData).unwrap();
          } catch {
            failedUploads += 1;
          }
        }

        if (failedUploads > 0) {
          setError(
            failedUploads === filesArray.length
              ? "Something went wrong"
              : `${failedUploads} file(s) failed to upload`,
          );
        } else {
          setError("");
        }

        if (failedUploads < filesArray.length) {
          toast("File successfully uploaded", {
            type: "success",
          });

          void refetch();
        }
      })();
    },
    [addFile, refetch, userInfoByEmail?.id, workId],
  );

  const handleOpenPreview = useCallback(
    (file: WorkFileTableItem) => {
      if (!file.id) {
        return;
      }

      const fileName = getFileName(file);

      setActiveFileName(fileName);
      setIsOpenPreview(true);
      void getPreviewFile(file.id);
    },
    [getPreviewFile],
  );

  const handleDownloadFile = useCallback(
    (file: WorkFileTableItem) => {
      if (!file.id) {
        toast("File download link is unavailable.", {
          type: "error",
        });

        return;
      }

      void (async () => {
        try {
          setDownloadingFileId(file.id);

          const previewResponse = await getDownloadFile(file.id).unwrap();
          const downloadUrl = previewResponse?.documentUrl;

          if (!downloadUrl) {
            toast("File download link is unavailable.", {
              type: "error",
            });

            return;
          }

          await downloadFileByUrl(downloadUrl, getFileName(file));
        } catch {
          toast("Something went wrong.", {
            type: "error",
          });
        } finally {
          setDownloadingFileId(null);
        }
      })();
    },
    [getDownloadFile],
  );

  const handleDeleteFile = useCallback(() => {
    if (!openDelete?.id) {
      return;
    }

    void (async () => {
      try {
        await deleteFile(openDelete.id).unwrap();

        toast("File successfully deleted", {
          type: "success",
        });

        setOpenDelete(null);
        void refetch();
      } catch {
        toast("Something went wrong.", {
          type: "error",
        });
      }
    })();
  }, [deleteFile, openDelete?.id, refetch]);

  const columns = useMemo<ColumnDef<WorkFileTableItem, unknown>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: (file) => getFileNameWithoutExtension(getFileName(file)),
        cell: ({ row }) => {
          const file = row.original;
          const fileName = getFileName(file);
          const displayName = getFileNameWithoutExtension(fileName);

          return (
            <div
              data-tooltip-id={FILE_NAME_TOOLTIP_ID}
              data-tooltip-content={fileName}
              className={
                "max-w-[360px] overflow-hidden text-ellipsis whitespace-nowrap font-medium text-[#3F5266]"
              }>
              {displayName}
            </div>
          );
        },
      },
      {
        id: "preview",
        header: "Preview",
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type={"button"}
            onClick={() => handleOpenPreview(row.original)}
            className={
              "text-purple flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
            }>
            <TbFileSearch className={"h-6 w-6"} />
          </button>
        ),
      },
      {
        id: "type",
        header: "Type",
        accessorFn: (file) => getFileExtension(getFileName(file)),
        cell: ({ row }) => (
          <span className={"uppercase"}>
            {getFileExtension(getFileName(row.original))}
          </span>
        ),
      },
      {
        id: "size",
        header: "Size",
        accessorFn: (file) => file.documentSize ?? 0,
        cell: ({ row }) => formatFileSize(row.original.documentSize),
      },
      {
        id: "createdDate",
        header: "Date added",
        accessorFn: (file) =>
          file.createdDate ? new Date(file.createdDate).getTime() : 0,
        cell: ({ row }) => formatDate(row.original.createdDate),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        meta: {
          className: "w-[96px]",
        },
        cell: ({ row }) => {
          const file = row.original;
          const isDownloading = downloadingFileId === file.id;

          return (
            <div className={"flex items-center justify-end gap-2"}>
              <button
                type={"button"}
                title={"Download file"}
                disabled={isDownloading}
                onClick={() => handleDownloadFile(file)}
                className={
                  "text-purple flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100 disabled:opacity-60"
                }>
                {isDownloading ? (
                  <AiOutlineLoading3Quarters
                    className={"h-5 w-5 animate-spin"}
                  />
                ) : (
                  <FiDownload className={"h-6 w-6"} />
                )}
              </button>

              <button
                type={"button"}
                title={"Delete file"}
                onClick={() => setOpenDelete(file)}
                className={
                  "text-purple flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
                }>
                <TrashSvg className={"h-6 w-6"} />
              </button>
            </div>
          );
        },
      },
    ],
    [downloadingFileId, handleDownloadFile, handleOpenPreview],
  );

  return (
    <>
      <Card extraClasses={"shadow-box max-h-full rounded-[6px]"}>
        <div className={"max-w-full pt-4"}>
          <div className={"w-full px-5 pb-3"}>
            <div className={"flex w-full items-center justify-between"}>
              <div className={"text-base font-semibold"}>Files</div>

              <InputFile
                setFile={handleFileChange}
                setIsOpenErrorPopUp={setIsOpenErrorPopUp}
                setError={setError}
                inputRef={addFileRef}
                isLoading={isLoadingAdd}
                multiple
                button={
                  <button
                    disabled={isLoadingAdd}
                    className={
                      "flex h-10 items-center justify-center gap-2 rounded-lg border border-[#6938EF] bg-[#6938EF] px-4 text-sm font-medium text-white"
                    }>
                    {isLoadingAdd ? (
                      <div
                        className={classNames(
                          "h-5 w-5 animate-spin text-white",
                        )}>
                        <AiOutlineLoading3Quarters size={20} />
                      </div>
                    ) : (
                      <>
                        <PlusSvg />
                        <span>Upload file</span>
                      </>
                    )}
                  </button>
                }
              />
            </div>

            {error && (
              <div className={"mt-2 text-sm font-semibold text-red-500"}>
                {error}
              </div>
            )}
          </div>

          {isLoading ? (
            <div className={"p-4"}>
              <LottieLoading />
            </div>
          ) : !filesData.length ? (
            <div className={"p-6"}>
              <NoItems
                icon={<ContactFileSvg />}
                message={"There are no files"}
              />
            </div>
          ) : (
            <div className={"max-h-[360px] overflow-y-auto px-2 pb-2"}>
              <Table
                columns={columns}
                data={filesData}
                getRowId={(file, index) =>
                  file.id || `${getFileName(file)}-${index}`
                }
                initialSorting={[{ id: "createdDate", desc: true }]}
              />
            </div>
          )}
        </div>
      </Card>

      <ReactTooltip
        id={FILE_NAME_TOOLTIP_ID}
        place={"bottom"}
        className={"!rounded-[8px] !p-2 !text-sm"}
      />

      {openDelete && (
        <SettingsConfirmModal
          onClose={() => setOpenDelete(null)}
          onConfirm={handleDeleteFile}
          isLoading={isLoadingDelete}
          icon={<TrashSvg />}
          title={`Delete ‘${getFileName(openDelete)}’ file?`}
          description={"Do you want to permanently delete this file?"}
          confirmText={"Delete"}
          variant={"danger"}
        />
      )}

      {isOpenPreview && (
        <PreviewFile
          close={() => setIsOpenPreview(false)}
          file={previewFile?.documentUrl || ""}
          fileName={activeFileName}
          isLoading={isLoadingPreview}
        />
      )}

      {isOpenErrorPopUp && (
        <SettingsConfirmModal
          noCancel
          confirmText={"Ok"}
          onClose={() => setIsOpenErrorPopUp(false)}
          onConfirm={() => setIsOpenErrorPopUp(false)}
          variant={"warning"}
          confirmButtonStyles={
            "flex w-[200px] ml-[45%] justify-center rounded-lg px-4 py-2 disabled:opacity-75"
          }
          icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
          title={"File Size Exceeds Limit"}
          description={
            "One or more selected file(s) is too large to upload. The maximum allowed size is 5MB. Please upload a smaller file and try again."
          }
        />
      )}
    </>
  );
};
